import React from 'react'
import { icons } from 'cdh-data-portal-theme'
import type { IconPathProps } from 'cdh-data-portal-theme'
import { Topbar } from './components/molecules/Topbar/Topbar'
import { Sidebar } from './components/molecules/Sidebar/Sidebar'
import { SidebarNavItem } from './components/atoms/SidebarNavItem/SidebarNavItem'
import { NAV_ITEMS } from './components/atoms/SidebarNavItem/SidebarNavItem.static'
import { CurrentUserProvider } from './contexts/CurrentUserContext'
import { AuthorizationProvider } from './contexts/AuthorizationContext'
import { Router } from './components/Router/Router'
import { Login } from './components/pages/Login/Login'
import { DeviceFlowLogin } from './components/pages/DeviceFlowLogin/DeviceFlowLogin'
import { AuthStatuses, useAuthStatus } from './hooks/authentication/useAuthStatus'
import { useAuthorization } from './hooks/authorization/useAuthorization'

function App() {
  const { redirectAfterLoginUrl, status, user } = useAuthStatus()
  const isSignedIn = status === AuthStatuses.authSignedIn
  const { authorization, deviceFlow } = useAuthorization(isSignedIn)

  if (status === AuthStatuses.authStart || status === AuthStatuses.authInProgress) {
    return null
  }

  if (status === AuthStatuses.authLoginRequired) {
    return <Login redirectAfterLoginUrl={redirectAfterLoginUrl} />
  }

  if (status === AuthStatuses.authFailed) {
    return <p role="alert">Could not load your user. Please try again.</p>
  }

  // Wait for the silent refresh/device flow to provide the token before mounting pages that call
  // the backend. Once the token is ready, role loading can continue alongside the app shell.
  const deviceFlowPending =
    deviceFlow.status === 'idle' ||
    deviceFlow.status === 'starting' ||
    deviceFlow.status === 'polling' ||
    deviceFlow.status === 'error'

  if (deviceFlowPending) {
    return <DeviceFlowLogin deviceFlow={deviceFlow} />
  }

  if (authorization.rolesError && !authorization.rolesLoaded) {
    return <p role="alert">Could not load your permissions. Please try again.</p>
  }

  // A user with no role from an allowed ops-hub account (see config/accounts.ts) can still see
  // the home page -- individual routes that require a role are gated in Router.tsx
  // (RequireAuthorizedRoute), not the app shell as a whole. Optimistically keep gated nav items
  // visible while roles are still loading (mirrors RequireAuthorizedRoute), and hide them only
  // once roles have conclusively come back empty.
  const isAuthorized = !authorization.rolesLoaded || authorization.roles.length > 0
  const visibleNavItems = NAV_ITEMS.filter((item) => isAuthorized || !item.requiresAuthorization)

  return (
    <main className="app-shell">
      <CurrentUserProvider value={user}>
        <AuthorizationProvider value={authorization}>
          <Topbar />
          <Sidebar>
            {visibleNavItems.map((item) => (
              <SidebarNavItem
                key={item.label}
                icon={icons[item.iconKey as keyof typeof icons] as IconPathProps}
                label={item.label}
                url={item.url || undefined}
              />
            ))}
          </Sidebar>
          <Router redirectAfterLoginUrl={redirectAfterLoginUrl} />
        </AuthorizationProvider>
      </CurrentUserProvider>
    </main>
  )
}

export default App
